import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { isIntercomDisabledForLocale } from '@/utils/intercomLocale';
import { logger } from '@/utils/logger/logger';
import Intercom, {
  hide as intercomHide,
  onHide as intercomOnHide,
  onShow as intercomOnShow,
  show as intercomShow,
  shutdown as intercomShutdown,
  trackEvent as intercomTrackEvent,
  update as intercomUpdate,
} from '@intercom/messenger-js-sdk';
import type * as IntercomTypes from '@intercom/messenger-js-sdk/dist/types';

let isInitialized = false;
let currentLocale: string | null | undefined;

const isIntercomGatedForLocale = (): boolean =>
  isIntercomDisabledForLocale(currentLocale);

export const IntercomId = 'ylu5iqz2';

export const configureIntercomLocale = (locale?: string | null) => {
  const wasDisabled = isIntercomDisabledForLocale(currentLocale);
  currentLocale = locale ?? null;
  const isDisabled = isIntercomDisabledForLocale(currentLocale);

  if (isDisabled) {
    shutDownIntercom();
    isInitialized = false;
  } else if (wasDisabled) {
    isInitialized = false;
  }
};

export function bootOrUpdate(settings?: IntercomTypes.IntercomSettings) {
  // TELEMETRY-GATE: F1 function-entry gate. Canonical pattern from
  // .ai-context/onprem-guardrails.md — Intercom Messenger SDK boots the
  // widget loader (widget.intercom.io) and the messenger API
  // (api-iam.intercom.io); short-circuit so neither is reachable on a
  // suppressed build, even if `setIntercomProperties` is called.
  if (!isSaasTelemetryEnabled || isIntercomGatedForLocale()) return;
  try {
    const base: IntercomTypes.IntercomSettings = {
      app_id: IntercomId,
      hide_default_launcher: true,
    };

    const payload = {
      ...base,
      ...(settings || {}),
    } as IntercomTypes.IntercomSettings;

    // Ensure SDK is initialized
    if (!isInitialized) {
      Intercom(payload);
      isInitialized = true;
    }

    // Update traits (SDK auto-boots with NPM initializer)
    intercomUpdate(payload);
  } catch (error) {
    logger.warn('Intercom boot/update failed', { error });
  }
}

export const openIntercom = () => {
  if (!isSaasTelemetryEnabled || isIntercomGatedForLocale()) return;
  bootOrUpdate();
  try {
    intercomShow();
  } catch (error) {
    logger.warn('Intercom show failed', { error });
  }
};

/**
 * Opens Intercom messenger with a pre-filled message in the composer.
 * The user can review/edit the message before sending.
 * @param message - The message to pre-fill in the composer
 */
export const showNewMessage = (message: string) => {
  if (!isSaasTelemetryEnabled || isIntercomGatedForLocale()) return;
  bootOrUpdate();
  try {
    // Use window.Intercom for the showNewMessage command as the SDK
    // doesn't expose this method directly
    const intercom = (window as { Intercom?: (cmd: string, arg?: string) => void })
      .Intercom;
    if (intercom) {
      intercom('showNewMessage', message);
    } else {
      logger.warn('Intercom showNewMessage: window.Intercom not available');
    }
  } catch (error) {
    logger.warn('Intercom showNewMessage failed', { error, message });
  }
};

export const hideIntercom = () => {
  if (!isSaasTelemetryEnabled) return;
  try {
    intercomHide();
  } catch (error) {
    logger.warn('Intercom hide failed', { error });
  }
};

export const trackEvent = (eventName: string) => {
  if (!isSaasTelemetryEnabled || isIntercomGatedForLocale()) return;
  try {
    intercomTrackEvent(eventName);
  } catch (error) {
    logger.warn('Intercom trackEvent failed', { error, eventName });
  }
};

export const onHideIntercom = (callbackFn: () => void) => {
  if (!isSaasTelemetryEnabled) return;
  try {
    intercomOnHide(() => {
      callbackFn();
    });
  } catch (error) {
    logger.warn('Intercom onHide hook failed', { error });
  }
};

export const onShowIntercom = (callbackFn: () => void) => {
  if (!isSaasTelemetryEnabled) return;
  try {
    intercomOnShow(() => {
      callbackFn();
    });
  } catch (error) {
    logger.warn('Intercom onShow hook failed', { error });
  }
};

// clear cookies and data for intercom and reset it
export const shutDownIntercom = () => {
  if (!isSaasTelemetryEnabled) return;
  try {
    intercomShutdown();
  } catch (error) {
    logger.warn('Intercom shutdown failed', { error });
  }
};
